import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import {
  KpiTile,
  KpiLoadingState,
  LoadingState,
  ErrorState,
  EmptyState,
  HubSection,
  StrengthBar,
  RecencyBadge,
  AskAboutThis,
  helpsHurts,
  toneText,
  pct,
  formatDay,
  safeDate,
} from "@/components/research-hub";
import { usePmBookImpact, usePmExposure, BRIDGED_SCENARIOS } from "@/hooks/useResearchHubPm";

type Row = Record<string, any>;

const GUARDRAIL_FALLBACK = "Decision support only — no position sizing.";

// Pick a sensible "effective" date from a loosely-typed effective_dates object.
function pickEffectiveDate(d: Row | undefined): string | null {
  if (!d || typeof d !== "object") return null;
  const candidates = [
    d.effective_date,
    d.as_of,
    d.as_of_date,
    d.book_effective_date,
    d.holdings_date,
    d.latest,
    d.max,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c;
  }
  // Fall back to the NEWEST date-parseable value on the object (a lexicographic sort would pick the
  // alphabetically-last string, e.g. "q4" over a real ISO date).
  let best: { v: string; t: number } | null = null;
  for (const v of Object.values(d)) {
    if (typeof v !== "string" || !v.trim()) continue;
    const t = safeDate(v)?.getTime();
    if (t == null) continue;
    if (!best || t > best.t) best = { v, t };
  }
  return best?.v ?? null;
}

// A reader-safe range name: the human label if present, else range/book_range only when it is NOT a
// bare internal code (P/R/…). Returns "" when nothing presentable. Shared by every place a range is
// shown so a raw code can't leak through one path.
function rangeName(r: Row): string {
  const label = r.range_label;
  if (typeof label === "string" && label.trim()) return label.trim();
  const raw = r.range ?? r.book_range;
  if (raw != null && String(raw).trim()) {
    const s = String(raw).trim();
    if (!/^[A-Za-z]{1,2}$/.test(s)) return s; // skip one/two-letter internal codes
  }
  return "";
}

// A small "Cautious · level 12" range-and-level chip.
function rangeLevel(r: Row): string {
  const parts: string[] = [];
  const rn = rangeName(r);
  if (rn) parts.push(rn);
  const level = r.portfolio ?? r.portfolio_level ?? r.level;
  if (level != null && level !== "") parts.push(`level ${level}`);
  return parts.join(" · ");
}

// Stable list key: one fund can appear at several range/levels, so fund_id alone collides.
function rowKey(r: Row, i: number): string {
  const range = r.range_label ?? r.range ?? r.book_range ?? "";
  const level = r.portfolio ?? r.portfolio_level ?? r.level ?? "";
  return `${r.fund_id ?? "f"}-${range}-${level}-${i}`;
}

// Plain-English exposure line: "this scenario helps / hurts".
function ExposureLine({ sign, magnitude }: { sign: any; magnitude: any }) {
  const hh = helpsHurts(sign);
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {hh.word ? (
        <span className={cn("font-body text-xs", toneText(hh.tone))}>this scenario {hh.word}</span>
      ) : (
        <span className="font-body text-xs text-muted-foreground">effect unclear</span>
      )}
      <StrengthBar value={magnitude} label="exposure" />
    </div>
  );
}

export default function PmBook() {
  const [scenario, setScenario] = useState<string>(BRIDGED_SCENARIOS[0]);
  const [showAll, setShowAll] = useState(false);

  const book = usePmBookImpact(scenario);
  const exposure = usePmExposure();

  const data = book.data;

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="font-heading text-xl font-semibold">Your book</h1>
          {/* /partner/research-hub/book/positioning was fully built but had no inbound link
              anywhere — URL-only. It is a sub-route of this page, so this is its natural home.
              Same <SurfaceGate surface="pm"> as this page, so anyone seeing the link can open it. */}
          <Button variant="outline" size="sm" asChild>
            <Link to="/partner/research-hub/book/positioning">Positioning</Link>
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {BRIDGED_SCENARIOS.map((sc) => (
            <button
              key={sc}
              type="button"
              onClick={() => {
                setScenario(sc);
                setShowAll(false);
              }}
              className={cn(
                "rounded-md border px-3 py-1.5 font-body text-sm transition-colors",
                sc === scenario
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:bg-muted/60",
              )}
            >
              {sc}
            </button>
          ))}
        </div>
      </div>

      {book.isLoading ? (
        <div className="space-y-6">
          <KpiLoadingState tiles={4} />
          <LoadingState rows={3} />
        </div>
      ) : book.error ? (
        <ErrorState onRetry={() => book.refetch()} />
      ) : !data || data.error ? (
        <BookScenarioError
          data={data}
          onChoose={(label) => {
            setScenario(label);
            setShowAll(false);
          }}
        />
      ) : (
        <BookBody
          data={data}
          scenario={scenario}
          showAll={showAll}
          onShowAll={() => setShowAll(true)}
          exposure={exposure}
        />
      )}
    </div>
  );
}

function BookBody({
  data,
  scenario,
  showAll,
  onShowAll,
  exposure,
}: {
  data: Row;
  scenario: string;
  showAll: boolean;
  onShowAll: () => void;
  exposure: ReturnType<typeof usePmExposure>;
}) {
  // Default to UNbridged unless the backend explicitly says bridged — an absent flag must not unlock
  // the modelled-loss tiles.
  const bridged = data.scenario_bridged === true;
  const resolved: Row = data.resolved_scenario ?? {};
  const scenarioLabel = resolved.label ?? scenario;
  const fundCount = data.mandate_fund_count;
  const positionCount = data.book_position_count;
  const effectiveDate = pickEffectiveDate(data.effective_dates);

  const quantified: Row[] = Array.isArray(data.quantified_impact) ? data.quantified_impact : [];
  const drawdownTiles = quantified.filter(
    (q) => typeof q.stressed_loss_pct === "number" && Number.isFinite(q.stressed_loss_pct),
  );
  const mostExposed: Row[] = Array.isArray(data.most_exposed) ? data.most_exposed : [];
  const positions: Row[] = Array.isArray(data.book_positions) ? data.book_positions : [];
  const notes = typeof data.notes === "string" && data.notes.trim() ? data.notes : GUARDRAIL_FALLBACK;

  // EMPTY: no mandate funds touch the research graph.
  if (positionCount === 0) {
    return (
      <div className="space-y-6">
        <EmptyState title="No mandate funds link to the research graph yet." />
        <p className="font-body text-[11px] text-muted-foreground">{notes}</p>
      </div>
    );
  }

  const visiblePositions = showAll ? positions : positions.slice(0, 50);

  return (
    <div className="space-y-8">
      {/* Summary band */}
      <Card className="p-5 space-y-2">
        <div className="font-heading text-lg font-medium">{scenarioLabel}</div>
        <div className="font-body text-sm text-muted-foreground">
          {fundCount != null && (
            <>
              {fundCount} fund{fundCount === 1 ? "" : "s"} in your mandate
            </>
          )}
          {effectiveDate && (
            <>
              {fundCount != null ? " · " : ""}as of {formatDay(effectiveDate)}
            </>
          )}
        </div>
        {!bridged && (
          <p className="font-body text-sm text-muted-foreground">
            Qualitative only — no modelled loss numbers.
          </p>
        )}
      </Card>

      {/* Modelled drawdown tiles (bridged only) — only for entries that actually carry a number, so we
          never render "about —" as a loss. */}
      {bridged && drawdownTiles.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {drawdownTiles.map((q: Row, i: number) => {
            const caption = rangeName(q) || (q.level != null ? `level ${q.level}` : "across the book");
            return (
              <KpiTile
                key={i}
                value={`about ${pct(q.stressed_loss_pct)}`}
                caption="estimated drawdown"
                secondary={caption}
                tone="bad"
              />
            );
          })}
        </div>
      )}

      {/* Where you're most exposed */}
      <HubSection title="Where you're most exposed">
        {mostExposed.length === 0 ? (
          <EmptyState title="Nothing stands out as especially exposed here." tone="good" />
        ) : (
          <div className="space-y-2">
            {mostExposed.map((r: Row, i: number) => {
              const label = r.label ?? r.fund_label ?? r.subject_label ?? "A position";
              const rl = rangeLevel(r);
              return (
                <Card key={rowKey(r, i)} className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-body text-sm truncate">{label}</div>
                      {rl && <div className="font-body text-xs text-muted-foreground">{rl}</div>}
                    </div>
                    <AskAboutThis prefill={`How is ${label} exposed to ${scenarioLabel}?`} label="" />
                  </div>
                  <ExposureLine sign={r.exposure_sign} magnitude={r.exposure_magnitude} />
                  <div className="flex items-center gap-3 flex-wrap">
                    {r.current_weight_pct != null && (
                      <span className="font-body text-xs text-muted-foreground">
                        {pct(r.current_weight_pct)} of the book
                      </span>
                    )}
                    {r.effective_date && <RecencyBadge date={r.effective_date} />}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </HubSection>

      {/* Loss attribution (bridged only) */}
      {bridged && <LossAttribution quantified={quantified} />}

      {/* Full book — collapsed, lazy */}
      <HubSection title="Your full book">
        <Accordion type="single" collapsible>
          <AccordionItem value="book" className="border rounded-lg px-3">
            <AccordionTrigger className="font-body text-sm hover:no-underline">
              {positionCount != null
                ? `All ${positionCount} position${positionCount === 1 ? "" : "s"}`
                : "All positions"}
            </AccordionTrigger>
            <AccordionContent>
              {positions.length === 0 ? (
                <EmptyState title="No positions to show for this scenario." />
              ) : (
                <div className="space-y-2 pt-1">
                  {visiblePositions.map((p: Row, i: number) => {
                    const label = p.fund_label ?? p.label ?? p.subject_label ?? "A fund";
                    const rl = rangeLevel(p);
                    return (
                      <div
                        key={rowKey(p, i)}
                        className="flex items-center justify-between gap-3 border-b border-border/60 pb-2 last:border-0 last:pb-0"
                      >
                        <div className="min-w-0">
                          <div className="font-body text-sm truncate">{label}</div>
                          {rl && <div className="font-body text-xs text-muted-foreground">{rl}</div>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <StrengthBar
                            value={p.exposure_magnitude}
                            label={helpsHurts(p.exposure_sign).word || "exposure"}
                            width="w-16"
                          />
                          {p.current_weight_pct != null && (
                            <span className="font-body text-xs text-muted-foreground w-14 text-right">
                              {pct(p.current_weight_pct)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {!showAll && positions.length > 50 && (
                    <div className="pt-2">
                      <Button variant="outline" size="sm" onClick={onShowAll}>
                        Show more ({positions.length - 50} more)
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </HubSection>

      {/* Why am I exposed? — drivers */}
      <HubSection title="Why am I exposed?" description="The research drivers that reach your funds.">
        {exposure.isLoading ? (
          <LoadingState rows={2} />
        ) : exposure.error ? (
          <ErrorState onRetry={() => exposure.refetch()} />
        ) : (exposure.data ?? []).length === 0 ? (
          <EmptyState title="No research drivers reach your funds yet." tone="good" />
        ) : (
          <div className="space-y-2">
            {exposure.data!.map((d: Row, i: number) => (
              <DriverRow key={i} d={d} />
            ))}
          </div>
        )}
      </HubSection>

      {/* Guardrail */}
      <p className="font-body text-[11px] text-muted-foreground">{notes}</p>
    </div>
  );
}

// Scenario didn't resolve. Mirror ScenarioTrace's recovery: offer "did you mean…" chips when the
// backend returns candidates, otherwise a calm fallback nudging the modelled scenarios.
function BookScenarioError({ data, onChoose }: { data: Row | undefined; onChoose: (label: string) => void }) {
  const ambiguity: Row[] = Array.isArray(data?.ambiguity) ? (data!.ambiguity as Row[]) : [];
  const chips = ambiguity
    .map((c) => (typeof c === "string" ? c : c?.label ?? c?.name ?? c?.title ?? ""))
    .filter((s): s is string => typeof s === "string" && s.trim().length > 0);

  if (chips.length === 0) {
    return <EmptyState title="We couldn't find that scenario — try one of the modelled ones." />;
  }

  return (
    <Card className="p-5 space-y-3">
      <p className="font-body text-sm text-muted-foreground">We couldn't pin that scenario down — did you mean…</p>
      <div className="flex flex-wrap gap-2">
        {chips.map((label, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChoose(label)}
            className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1 font-body text-sm text-primary hover:bg-primary/10 transition-colors"
          >
            {label}
          </button>
        ))}
      </div>
    </Card>
  );
}

function LossAttribution({ quantified }: { quantified: Row[] }) {
  const entries = quantified.filter(
    (q) => Array.isArray(q.top_contributors) && q.top_contributors.length > 0,
  );
  if (entries.length === 0) return null;

  return (
    <HubSection title="Where the loss comes from" description="Where the modelled loss comes from.">
      <div className="space-y-3">
        {entries.map((q: Row, qi: number) => {
          const heading = rangeName(q) || (q.level != null ? `level ${q.level}` : null);
          const contributors: Row[] = q.top_contributors;
          return (
            <Card key={qi} className="p-4 space-y-2">
              {heading && (
                <div className="font-body text-xs text-muted-foreground">By sector · {String(heading)}</div>
              )}
              {!heading && <div className="font-body text-xs text-muted-foreground">By sector</div>}
              <ul className="space-y-1.5">
                {contributors.map((c: Row, ci: number) => (
                  <li key={ci} className="flex items-center justify-between gap-3 font-body text-sm">
                    <span className="truncate">{c.ia_sector ?? "A sector"}</span>
                    <span className="text-muted-foreground shrink-0">
                      contributes about {pct(c.contribution_pct, 1)}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}
      </div>
    </HubSection>
  );
}

function DriverRow({ d }: { d: Row }) {
  const label = d.driver_label ?? "A research driver";
  const hh = helpsHurts(d.sign);
  const reached = d.reached_fund_count;
  const path: string[] = Array.isArray(d.exemplar_path_labels)
    ? d.exemplar_path_labels.filter((x: any) => typeof x === "string" && x.trim())
    : [];

  return (
    <Card className="p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-body text-sm truncate">{label}</div>
          <div className="font-body text-xs text-muted-foreground">
            {reached != null && <>reaches {reached} of your funds</>}
            {hh.word && (
              <>
                {reached != null ? " · " : ""}
                <span className={toneText(hh.tone)}>this scenario {hh.word}</span>
              </>
            )}
          </div>
        </div>
        <AskAboutThis prefill={`Why does ${label} reach my funds?`} label="" />
      </div>
      <StrengthBar value={d.mean_propagation_score} label="transmission" />
      {path.length > 0 && (
        <div className="font-body text-xs text-muted-foreground truncate">{path.join(" → ")}</div>
      )}
      {d.conflict_flag === true && (
        <div className="font-body text-[11px] text-[hsl(var(--financial-gold))]">
          crosses a disputed link
        </div>
      )}
    </Card>
  );
}
